import React, { useState, useEffect } from 'react';
import { FileBadge, MapPin, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import MultiImageUploader from './MultiImageUploader';
import { listCarImages, saveCarImages } from '../utils/carImages';
import { getTimestamp, sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import BrandSelector from './BrandSelector';
import GpsDevicePicker from './GpsDevicePicker';
import { parsePlate, buildStructuredPlate, PLATE_LETTERS } from '../utils/plateUtils';

export default function CarForm({ car, cars, settings, onSave, onClose }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const [data, setData] = useState(car ? { chassisNumber: '', ...car } : {
    brand: '', model: '', year: '', plate: '', km: 0, fuel: 'Essence',
    dailyRate: 0, status: 'available', credit: false, creditTotal: 0,
    creditRemaining: 0, monthlyPayment: 0, totalRented: 0,
    city: '',
    taxExpiry: '',
    insuranceExpiry: '',
    techVisitExpiry: '',
    traccarUniqueId: '',
    photo: '',
    bankInstallments: [],
    chassisNumber: ''
  });

  const parsed = parsePlate(car?.plate);
  const [plateMode, setPlateMode] = useState(parsed.mode); // 'standard' | 'ww' | 'free'
  const [plateParts, setPlateParts] = useState({
    num: parsed.num,
    letter: parsed.mode === 'ww' ? 'أ' : parsed.letter, // keep a real letter for standard mode
    province: parsed.province,
  });
  // WW plate has its own number + optional series, kept separate from standard.
  const [wwParts, setWwParts] = useState({
    num: parsed.mode === 'ww' ? parsed.num : '',
    series: parsed.mode === 'ww' ? parsed.province : '',
  });
  const [plateFree, setPlateFree] = useState(parsed.mode === 'free' ? (car?.plate || '') : '');

  // Structured input (number + arabic letter / WW / none + optional province).
  const handlePlateChange = (field, value) => {
    let sanitizedValue = value;
    if (field === 'num' || field === 'province') {
      sanitizedValue = value.replace(/[^0-9]/g, '');
    }
    setPlateParts(prev => {
      const updated = { ...prev, [field]: sanitizedValue };
      setData(d => ({ ...d, plate: buildStructuredPlate(updated) }));
      return updated;
    });
  };

  // WW (temporary) plate: number + fixed "WW" + optional series. No province.
  const handleWwChange = (field, value) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setWwParts(prev => {
      const updated = { ...prev, [field]: sanitized };
      setData(d => ({ ...d, plate: buildStructuredPlate({ num: updated.num, letter: 'WW', province: updated.series }) }));
      return updated;
    });
  };

  // "Numbers Only" mode — strictly numeric digits, no Arabic letters / no code.
  const handlePlateFreeChange = (value) => {
    const digitsOnly = value.replace(/[^0-9]/g, '');
    setPlateFree(digitsOnly);
    setData(d => ({ ...d, plate: digitsOnly }));
  };

  // Switching modes recomputes the stored plate from the active editor.
  const switchPlateMode = (mode) => {
    setPlateMode(mode);
    if (mode === 'standard') {
      setData(d => ({ ...d, plate: buildStructuredPlate(plateParts) }));
    } else if (mode === 'ww') {
      setData(d => ({ ...d, plate: buildStructuredPlate({ num: wwParts.num, letter: 'WW', province: wwParts.series }) }));
    } else {
      const seed = plateFree || data.plate || '';
      setPlateFree(seed);
      setData(d => ({ ...d, plate: seed.trim() }));
    }
  };

  const [saving, setSaving] = useState(false);

  // ── Multi-image gallery ──────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (car?.id) {
        const existing = await listCarImages(car.id);
        if (!alive) return;
        if (existing.length) { setImages(existing); setImagesLoaded(true); return; }
      }
      if (!alive) return;
      // Seed from the legacy single photo when there are no gallery rows yet.
      if (car?.photo) setImages([{ url: car.photo, isCover: true, order: 0 }]);
      setImagesLoaded(true);
    })();
    return () => { alive = false; };
  }, [car?.id]);

  const submit = async () => {
    if (saving) return;
    if (!data.brand) {
      return alert(isAr ? 'الماركة مطلوبة' : 'Marque requise');
    }
    // Plate: only the number is required. City/province/series code is optional,
    // and plates with numbers only are allowed.
    if (!(data.plate || '').trim()) {
      return alert(t('plate_invalid') || (isAr ? 'يرجى إدخال رقم اللوحة' : 'Veuillez saisir le numéro de la plaque'));
    }
    // Chassis (VIN) is OPTIONAL. Only check for duplicates when a value is given.
    if (data.chassisNumber && data.chassisNumber.trim() && cars && Array.isArray(cars)) {
      const chassisUpper = data.chassisNumber.trim().toUpperCase();
      const duplicate = cars.find(c => c.chassisNumber && c.chassisNumber.trim().toUpperCase() === chassisUpper && c.id !== car?.id);
      if (duplicate) {
        return alert(t('chassis_number_duplicate') || (isAr ? 'رقم الهيكل هذا مسجل بالفعل لسيارة أخرى!' : 'Ce numéro de châssis existe déjà !'));
      }
    }
    
    const payload = {
      ...data,
      km: parseInt(data.km) || 0,
      dailyRate: parseFloat(data.dailyRate) || 0,
      creditTotal: parseFloat(data.creditTotal) || 0,
      creditRemaining: parseFloat(data.creditRemaining) || 0,
      monthlyPayment: parseFloat(data.monthlyPayment) || 0,
    };

    setSaving(true);
    try {
      let carId = car?.id;
      if (car) {
        await onSave(car.id, payload);
      } else {
        const created = await onSave(null, { ...payload, createdAt: getTimestamp() });
        carId = (created && (created.id || created._id)) || carId;
        try {
          await sendTelegramNotification(buildTelegramMessage('new_car', { brand: data.brand, model: data.model, plate: data.plate }), 'new_car');
        } catch (_) {}
      }

      // Persist the image gallery and mirror the cover onto car.photo so every
      // existing place that reads car.photo keeps working. Guarded by
      // imagesLoaded so an early save never wipes a car's existing gallery.
      if (carId && imagesLoaded) {
        try {
          const agencyId = (typeof window !== 'undefined' && window.__AGENCY_ID__) || null;
          const { coverUrl } = await saveCarImages(carId, agencyId, images);
          if ((coverUrl || '') !== (payload.photo || '')) {
            await onSave(carId, { photo: coverUrl || '' });
          }
        } catch (imgErr) {
          console.error('Car images save error:', imgErr);
          alert('⚠️ ' + (isAr ? 'تعذّر حفظ بعض الصور' : 'Certaines images n\'ont pas pu être enregistrées') + ' : ' + (imgErr.message || ''));
        }
      }
      onClose();
    } catch (err) {
      console.error('Car save error:', err);
      const msg = (err && (err.message || err.error_description || err.hint)) || 'Erreur inconnue';
      alert('❌ ' + msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={car ? t('edit_car') : t('new_car')}>
      <div>
        {/* PHOTOS VOITURE — galerie multi-images */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📸 {isAr ? 'صور السيارة (عدة صور)' : 'Photos de la voiture (plusieurs)'}
          </label>
          <MultiImageUploader value={images} onChange={setImages} isAr={isAr} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <BrandSelector value={data.brand} onChange={v => setData(prev => ({ ...prev, brand: v }))} />
          <Field label={t('model_req')} value={data.model} onChange={v => setData(prev => ({ ...prev, model: v }))} />
          <Field label={t('year_label')} value={data.year} onChange={v => setData(prev => ({ ...prev, year: v }))} />
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('plate_req') || (isAr ? 'رقم اللوحة *' : 'Plaque d\'immatriculation *')}
              </label>
              {/* Plate type selector: complete (city) / WW / free numbers */}
              <div className="inline-flex rounded-md overflow-hidden border border-gray-300 text-[11px] font-semibold">
                {[
                  { id: 'standard', fr: 'Complète', ar: 'كاملة' },
                  { id: 'ww',       fr: 'WW',       ar: 'WW' },
                  { id: 'free',     fr: 'Numéros',  ar: 'أرقام' },
                ].map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => switchPlateMode(m.id)}
                    className={`px-2.5 py-1 border-e last:border-e-0 border-gray-300 ${plateMode === m.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                  >
                    {isAr ? m.ar : m.fr}
                  </button>
                ))}
              </div>
            </div>

            {plateMode === 'standard' ? (
              <>
                <div className="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-shadow">
                  {/* Registration Number */}
                  <input
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    value={plateParts.num}
                    onChange={e => handlePlateChange('num', e.target.value)}
                    placeholder="12345"
                    className="w-1/2 px-4 py-3 text-center text-base font-bold font-mono outline-none border-e border-gray-200"
                    title={isAr ? 'رقم التسجيل' : 'Numéro d\'enregistrement'}
                  />
                  {/* Arabic Letter */}
                  <select
                    value={plateParts.letter}
                    onChange={e => handlePlateChange('letter', e.target.value)}
                    className="w-1/4 py-3 text-center text-base font-extrabold bg-blue-50 text-blue-800 outline-none border-e border-gray-200 cursor-pointer appearance-none"
                    style={{ textAlignLast: 'center' }}
                    title={isAr ? 'الحرف العربي' : 'Lettre arabe'}
                  >
                    {PLATE_LETTERS.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  {/* Province code — optional */}
                  <input
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    value={plateParts.province}
                    onChange={e => handlePlateChange('province', e.target.value)}
                    placeholder="67"
                    className="w-1/4 px-4 py-3 text-center text-base font-bold font-mono outline-none"
                    title={isAr ? 'رمز الإقليم (اختياري)' : 'Code de province (optionnel)'}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {isAr ? 'صيغة رسمية: 12345 - أ - 67 (رمز الإقليم اختياري)' : 'Format officiel : 12345 - أ - 67 (province optionnelle)'}
                </p>
              </>
            ) : plateMode === 'ww' ? (
              <>
                <div className="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-shadow">
                  {/* Registration Number */}
                  <input
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    value={wwParts.num}
                    onChange={e => handleWwChange('num', e.target.value)}
                    placeholder="123456"
                    className="flex-1 px-4 py-3 text-center text-base font-bold font-mono outline-none border-e border-gray-200"
                    title={isAr ? 'رقم التسجيل' : 'Numéro d\'enregistrement'}
                  />
                  {/* Fixed WW badge */}
                  <span className="px-4 py-3 text-base font-extrabold bg-amber-50 text-amber-700 border-e border-gray-200 select-none">WW</span>
                  {/* Optional series */}
                  <input
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    value={wwParts.series}
                    onChange={e => handleWwChange('series', e.target.value)}
                    placeholder={isAr ? 'سلسلة' : 'série'}
                    className="w-1/4 px-3 py-3 text-center text-base font-bold font-mono outline-none"
                    title={isAr ? 'رقم السلسلة (اختياري)' : 'Numéro de série (optionnel)'}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {isAr ? 'لوحة مؤقتة WW: بدون رمز مدينة — مثال 123456 WW' : 'Plaque temporaire WW : sans code ville — ex. 123456 WW'}
                </p>
              </>
            ) : (
              <>
                <input
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  value={plateFree}
                  onChange={e => handlePlateFreeChange(e.target.value)}
                  placeholder={isAr ? 'مثال: 123456' : 'Ex : 123456'}
                  className="w-full px-4 py-3 text-center text-base font-bold font-mono border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  title={isAr ? 'أرقام فقط' : 'Chiffres uniquement'}
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {isAr ? 'أرقام فقط — بدون حروف عربية أو رمز مدينة.' : 'Chiffres uniquement — sans lettre arabe ni code ville.'}
                </p>
              </>
            )}
          </div>
          <Field 
            label={isAr ? 'رقم الهيكل (VIN)' : 'N° de Châssis (VIN)'}
            value={data.chassisNumber || ''} 
            onChange={v => setData(prev => ({ ...prev, chassisNumber: v }))} 
            placeholder={isAr ? 'أدخل رقم الهيكل' : 'Entrez le numéro de châssis'}
          />
          
          <Field 
            label={`📍 ${isAr ? 'المدينة' : 'Ville'}`} 
            value={data.city || ''} 
            onChange={v => setData(prev => ({ ...prev, city: v }))} 
            placeholder={isAr ? 'مثال: العيون' : 'Ex: Laayoune'} 
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('km_label')}</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={data.km || ''}
                onChange={e => setData(prev => ({ ...prev, km: e.target.value }))}
                className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
              {(car?.gpsOdometer || data.gpsOdometer) ? (
                <button
                  type="button"
                  onClick={() => {
                    const gpsOdo = car?.gpsOdometer || data.gpsOdometer || 0;
                    const currentKm = parseFloat(data.km) || 0;
                    const newKm = Math.round(currentKm + gpsOdo);
                    if (confirm(isAr
                      ? `إضافة ${gpsOdo.toFixed(1)} كم من GPS إلى ${currentKm} كم؟\nالمجموع الجديد: ${newKm} كم`
                      : `Ajouter ${gpsOdo.toFixed(1)} km du GPS à ${currentKm} km?\nNouveau total: ${newKm} km`)) {
                      setData(prev => ({ ...prev, km: String(newKm), gpsOdometer: 0 }));
                    }
                  }}
                  className="px-3 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg text-xs font-bold whitespace-nowrap transition"
                  title={isAr ? 'إضافة كم GPS إلى العداد' : 'Ajouter km GPS au compteur'}
                >
                  📡 +{(car?.gpsOdometer || 0).toFixed(0)} km
                </button>
              ) : null}
            </div>
            {car && (car.gpsOdometer || car.gpsKmDaily) && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-950">
                <div className="font-bold text-green-800 mb-1 flex items-center gap-1">
                  <span>📡</span> {isAr ? 'متابعة GPS:' : 'Suivi GPS:'}
                </div>
                {car.gpsOdometer && (
                  <div className="font-mono">{isAr ? `كم منذ آخر مزامنة: ${car.gpsOdometer.toFixed(1)} كم` : `Km depuis sync: ${car.gpsOdometer.toFixed(1)} km`}</div>
                )}
                {car.gpsKmDaily && (
                  <div className="font-mono text-blue-700">{isAr ? `اليوم: ${car.gpsKmDaily.toFixed(1)} كم` : `Aujourd'hui: ${car.gpsKmDaily.toFixed(1)} km`}</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel_label')}</label>
            <select 
              value={data.fuel} 
              onChange={e => setData(prev => ({ ...prev, fuel: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 outline-none text-base"
            >
              {['Essence', 'Diesel', 'Hybride', 'Électrique'].map(f => (
                <option key={f} value={f}>{t('fuel_' + f.toLowerCase()) || f}</option>
              ))}
            </select>
          </div>

          <Field 
            label={`${t('price_day')} (${settings.currency})`} 
            type="number" 
            value={data.dailyRate} 
            onChange={v => setData(prev => ({ ...prev, dailyRate: v }))} 
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('col_status')}</label>
            <select 
              value={data.status} 
              onChange={e => setData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 outline-none text-base"
            >
              <option value="available">{t('status_available') || 'Disponible'}</option>
              <option value="rented">{t('status_rented') || 'Louée'}</option>
              <option value="maintenance">{t('status_maintenance') || 'Entretien'}</option>
            </select>
          </div>
        </div>

        {/* DOCUMENTS OFFICIELS */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2 text-sm">
            <FileBadge className="w-4 h-4" />
            <span>{isAr ? '📋 الوثائق الرسمية (تواريخ انتهاء الصلاحية)' : '📋 Documents Officiels (Dates d\'expiration)'}</span>
          </h3>
          <p className="text-xs text-blue-700 mb-3">
            {isAr ? '🔔 ستتلقى إشعارات قبل 15 و 5 أيام و 24 ساعة من انتهاء الصلاحية' : '🔔 Vous recevrez des notifications 15j, 5j et 24h avant expiration'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">🏛️ Dariba / Vignette</label>
              <input
                type="date"
                value={data.taxExpiry || ''}
                onChange={e => setData(prev => ({ ...prev, taxExpiry: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">🛡️ Assurance</label>
              <input
                type="date"
                value={data.insuranceExpiry || ''}
                onChange={e => setData(prev => ({ ...prev, insuranceExpiry: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">🔧 Visite Technique</label>
              <input
                type="date"
                value={data.techVisitExpiry || ''}
                onChange={e => setData(prev => ({ ...prev, techVisitExpiry: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                🛢️ {isAr ? 'تغيير الزيت (كم)' : 'Vidange (km)'}
              </label>
              <input
                type="number"
                value={data.vidangeNextKm || ''}
                onChange={e => setData(prev => ({ ...prev, vidangeNextKm: e.target.value }))}
                placeholder={isAr ? 'الكيلومتراج القادم' : 'Prochain km'}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
              />
              {data.vidangeNextKm && data.km && (() => {
                const remaining = parseInt(data.vidangeNextKm) - parseInt(data.km || 0);
                if (remaining <= 0) return <p className="text-[10px] text-red-650 font-bold mt-1">⛔ {isAr ? 'متجاوزة!' : 'Dépassée!'}</p>;
                if (remaining <= 500) return <p className="text-[10px] text-red-500 font-bold mt-1">🚨 {remaining} km</p>;
                if (remaining <= 1000) return <p className="text-[10px] text-amber-600 font-bold mt-1">⚠️ {remaining} km</p>;
                return <p className="text-[10px] text-green-600 mt-1">✓ {remaining} km</p>;
              })()}
            </div>
          </div>
        </div>

        {/* GPS INTEGRATION */}
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4" />
            <span>📡 GPS Tracking (Athuraya)</span>
          </h3>
          <p className="text-xs text-green-700 mb-3">
            {isAr ? 'اربط هذه السيارة بجهاز GPS من حساب الثريا الخاص بك.' : 'Liez cette voiture à un dispositif GPS de votre compte Athuraya.'}
          </p>
          <GpsDevicePicker
            settings={settings}
            value={data.traccarUniqueId || ''}
            onChange={(val) => setData(prev => ({ ...prev, traccarUniqueId: val }))}
          />

          {/* Notifications (capot, geofence, vitesse, etc.) are ON by default
              for every vehicle — managed globally in Paramètres, not here. */}
        </div>

        {/* BANK CREDIT DETAILS */}
        <div className="mt-4 p-4 bg-amber-55 bg-amber-50 border border-amber-200 rounded-lg text-sm text-gray-900">
          <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
            <input 
              type="checkbox" 
              checked={data.credit}
              onChange={e => setData(prev => ({ ...prev, credit: e.target.checked }))} 
              className="w-4 h-4 cursor-pointer" 
            />
            <span className="font-bold text-gray-800">{t('car_has_credit') || (isAr ? 'السيارة بها قرض بنكي' : 'La voiture a un crédit')}</span>
          </label>
          
          {data.credit && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <Field 
                  label={t('credit_total') || (isAr ? 'إجمالي القرض' : 'Total crédit')} 
                  type="number" 
                  value={data.creditTotal} 
                  onChange={v => setData(prev => ({ ...prev, creditTotal: v }))} 
                />
                <Field 
                  label={t('credit_remaining') || (isAr ? 'المبلغ المتبقي' : 'Crédit restant')} 
                  type="number" 
                  value={data.creditRemaining} 
                  onChange={v => setData(prev => ({ ...prev, creditRemaining: v }))} 
                />
                <Field 
                  label={t('monthly_payment') || (isAr ? 'الدفع الشهري' : 'Mensualité')} 
                  type="number" 
                  value={data.monthlyPayment} 
                  onChange={v => setData(prev => ({ ...prev, monthlyPayment: v }))} 
                />
              </div>

              {/* Traites list */}
              <div className="mt-4 p-4 bg-white border-2 border-amber-300 rounded-xl">
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h4 className="font-bold text-amber-900 flex items-center gap-2">
                      🏦 {isAr ? 'الأقساط البنكية (Traites)' : 'Échéances bancaires (Traites)'}
                    </h4>
                    <p className="text-xs text-amber-700 mt-1">
                      {isAr 
                        ? 'أدخل تواريخ كل قسط. ستصلك تذكيرات تلقائية قبل كل موعد.'
                        : 'Entrez les dates de chaque traite. Vous recevrez des rappels automatiques avant chaque échéance.'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const total = parseFloat(data.creditTotal) || 0;
                        const monthly = parseFloat(data.monthlyPayment) || 0;
                        if (!total || !monthly) {
                          alert(isAr 
                            ? 'يرجى ملء المبلغ الإجمالي والقسط الشهري أولاً'
                            : 'Veuillez remplir le crédit total et la mensualité d\'abord');
                          return;
                        }
                        const startStr = prompt(
                          isAr 
                            ? 'تاريخ أول قسط (YYYY-MM-DD):'
                            : 'Date du premier paiement (YYYY-MM-DD):',
                          new Date().toISOString().split('T')[0]
                        );
                        if (!startStr) return;
                        
                        const nbInstallments = Math.ceil(total / monthly);
                        const installments = [];
                        const startDate = new Date(startStr);
                        
                        for (let i = 0; i < nbInstallments; i++) {
                          const d = new Date(startDate);
                          d.setMonth(d.getMonth() + i);
                          const amount = (i === nbInstallments - 1) 
                            ? total - (monthly * (nbInstallments - 1))
                            : monthly;
                          installments.push({
                            id: `inst_${Date.now()}_${i}`,
                            dueDate: d.toISOString().split('T')[0],
                            amount: amount,
                            paid: false,
                            paidDate: null,
                            paidAmount: 0,
                            note: ''
                          });
                        }
                        
                        setData(prev => ({ ...prev, bankInstallments: installments }));
                      }}
                      className="bg-purple-600 hover:bg-purple-750 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition shadow-sm"
                    >
                      ⚡ {isAr ? 'إنشاء تلقائي' : 'Auto-générer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newInstallment = {
                          id: `inst_${Date.now()}`,
                          dueDate: new Date().toISOString().split('T')[0],
                          amount: parseFloat(data.monthlyPayment) || 0,
                          paid: false,
                          paidDate: null,
                          paidAmount: 0,
                          note: ''
                        };
                        setData(prev => ({ 
                          ...prev, 
                          bankInstallments: [...(prev.bankInstallments || []), newInstallment] 
                        }));
                      }}
                      className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition shadow-sm"
                    >
                      + {isAr ? 'إضافة قسط' : 'Ajouter'}
                    </button>
                  </div>
                </div>

                {(data.bankInstallments || []).length === 0 ? (
                  <div className="text-center py-6 text-amber-600 text-sm bg-amber-50 rounded-lg">
                    {isAr 
                      ? '📅 لا توجد أقساط بعد. اضغط "إنشاء تلقائي" أو "إضافة قسط".'
                      : '📅 Aucune échéance. Cliquez sur "Auto-générer" ou "Ajouter".'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {(data.bankInstallments || []).map((inst, idx) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dueDate = new Date(inst.dueDate);
                      dueDate.setHours(0, 0, 0, 0);
                      const daysUntil = Math.ceil((dueDate - today) / 86400000);
                      
                      let statusColor = 'bg-gray-50 border-gray-200';
                      let statusBadge = null;
                      
                      if (inst.paid) {
                        statusColor = 'bg-green-50 border-green-300';
                        statusBadge = (
                          <span className="px-2 py-0.5 bg-green-105 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                            ✓ {isAr ? 'مدفوع' : 'Payé'}
                          </span>
                        );
                      } else if (daysUntil < 0) {
                        statusColor = 'bg-red-50 border-red-400 ring-2 ring-red-105 ring-red-100';
                        statusBadge = (
                          <span className="px-2 py-0.5 bg-red-650 text-white text-[10px] font-bold rounded-full animate-pulse">
                            🚨 {isAr ? `متأخر ${Math.abs(daysUntil)}ي` : `Retard ${Math.abs(daysUntil)}j`}
                          </span>
                        );
                      } else if (daysUntil <= 3) {
                        statusColor = 'bg-orange-55 bg-orange-50 border-orange-300';
                        statusBadge = (
                          <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                            ⚠️ {isAr ? `${daysUntil}ي متبقي` : `Dans ${daysUntil}j`}
                          </span>
                        );
                      } else if (daysUntil <= 7) {
                        statusColor = 'bg-yellow-50 border-yellow-300';
                        statusBadge = (
                          <span className="px-2 py-0.5 bg-yellow-500 text-white text-[10px] font-bold rounded-full">
                            📅 {isAr ? `${daysUntil}ي` : `${daysUntil}j`}
                          </span>
                        );
                      } else {
                        statusBadge = (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] font-bold rounded-full">
                            {isAr ? `${daysUntil}ي` : `${daysUntil}j`}
                          </span>
                        );
                      }

                      return (
                        <div key={inst.id || idx} className={`p-3 rounded-lg border ${statusColor} transition-colors`}>
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-12 sm:col-span-1 font-bold text-gray-500 text-sm">#{idx + 1}</div>
                            <div className="col-span-6 sm:col-span-3">
                              <label className="text-[10px] font-bold text-gray-500 block mb-0.5">{isAr ? 'تاريخ' : 'Date'}</label>
                              <input
                                type="date"
                                value={inst.dueDate}
                                disabled={inst.paid}
                                onChange={e => {
                                  const updated = [...data.bankInstallments];
                                  updated[idx] = { ...inst, dueDate: e.target.value };
                                  setData(prev => ({ ...prev, bankInstallments: updated }));
                                }}
                                className={`w-full px-2 py-1 border rounded text-xs font-mono outline-none ${inst.paid ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-900 focus:border-blue-400'}`}
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                              <label className="text-[10px] font-bold text-gray-500 block mb-0.5">{isAr ? 'المبلغ' : 'Montant'}</label>
                              <input
                                type="number"
                                value={inst.amount}
                                disabled={inst.paid}
                                onChange={e => {
                                  const updated = [...data.bankInstallments];
                                  updated[idx] = { ...inst, amount: parseFloat(e.target.value) || 0 };
                                  setData(prev => ({ ...prev, bankInstallments: updated }));
                                }}
                                className={`w-full px-2 py-1 border rounded text-xs font-mono font-bold outline-none ${inst.paid ? 'bg-gray-100 text-gray-450' : 'bg-white text-gray-900 focus:border-blue-400'}`}
                              />
                            </div>
                            <div className="col-span-4 sm:col-span-2 flex items-center justify-start sm:justify-center">{statusBadge}</div>
                            <div className="col-span-8 sm:col-span-3 flex gap-1 justify-end">
                              {!inst.paid ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...data.bankInstallments];
                                    updated[idx] = { 
                                      ...inst, 
                                      paid: true, 
                                      paidDate: new Date().toISOString().split('T')[0],
                                      paidAmount: inst.amount
                                    };
                                    setData(prev => ({ ...prev, bankInstallments: updated }));
                                  }}
                                  className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded transition"
                                >
                                  {isAr ? 'دفع' : 'Payer'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...data.bankInstallments];
                                    updated[idx] = { ...inst, paid: false, paidDate: null, paidAmount: 0 };
                                    setData(prev => ({ ...prev, bankInstallments: updated }));
                                  }}
                                  className="px-2 py-1 bg-gray-300 hover:bg-gray-450 text-gray-700 text-xs font-bold rounded transition"
                                >
                                  {isAr ? 'إلغاء' : 'Annuler'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  if (confirm(isAr ? 'حذف هذا القسط؟' : 'Supprimer cette échéance?')) {
                                    setData(prev => ({ 
                                      ...prev, 
                                      bankInstallments: prev.bankInstallments.filter((_, i) => i !== idx)
                                    }));
                                  }
                                }}
                                className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold rounded transition"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          {inst.paid && inst.paidDate && (
                            <div className="mt-2 text-xs text-green-700 font-bold">
                              ✓ {isAr ? `دُفع في ${inst.paidDate}` : `Payé le ${inst.paidDate}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Summary stats */}
                {(data.bankInstallments || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200 grid grid-cols-4 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="font-bold text-gray-550 text-gray-500">{isAr ? 'الإجمالي' : 'Total'}</div>
                      <div className="font-bold text-gray-800 font-mono">
                        {`${(data.bankInstallments || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(0)} ${settings.currency}`}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <div className="font-bold text-green-700">{isAr ? 'مدفوع' : 'Payé'}</div>
                      <div className="font-bold text-green-800 font-mono">
                        {`${(data.bankInstallments || []).filter(i => i.paid).reduce((s, i) => s + (parseFloat(i.paidAmount) || parseFloat(i.amount) || 0), 0).toFixed(0)} ${settings.currency}`}
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2">
                      <div className="font-bold text-amber-700">{isAr ? 'متبقي' : 'Restant'}</div>
                      <div className="font-bold text-amber-800 font-mono">
                        {`${(data.bankInstallments || []).filter(i => !i.paid).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(0)} ${settings.currency}`}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2">
                      <div className="font-bold text-blue-700">{isAr ? 'العدد' : 'Nombre'}</div>
                      <div className="font-bold text-blue-800 font-mono">
                        {`${(data.bankInstallments || []).filter(i => i.paid).length} / ${(data.bankInstallments || []).length}`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6 border-t border-gray-100 pt-4">
          <button 
            onClick={onClose} 
            disabled={saving} 
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-bold hover:bg-gray-50 text-gray-700 transition disabled:opacity-50 text-sm"
          >
            {t('cancel_btn')}
          </button>
          <button 
            onClick={submit} 
            disabled={saving} 
            className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-xl font-bold shadow-md shadow-blue-500/20 transition disabled:opacity-50 text-sm"
          >
            {saving ? '⏳ ...' : t('save_btn')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
